import { createSlice } from "@reduxjs/toolkit";
import BlockStates from "constants/BlockStates";
import SidemenuOptions, { STUDIO_TYPE } from "constants/SidemenuOptions";
import { isNull, isUndefined } from "lodash";
import { addEdge, applyNodeChanges } from "reactflow";
import { isExact } from "utils/isEqual";
import { logger } from "utils/logger";
import { v4 as uuid } from "uuid";
import DefaultPipe from "../../assets/images/default-pipe.png";

const initialState = {
  activeTab: null,
  tabs: {},
  hasMoved: false,
};

const NODE_LABEL = {
  pipe: "pipelines",
  function: "Hi",
};

/**
 * Generates a unique identifier (UUID) with the prefix "fl-".
 * The UUID consists of a random string of lowercase letters and digits.
 * 
 * @returns {string} The generated UUID.
 */
export function generateUUID() {
  const characters = uuid();
  let indicator = "fl-";

  return indicator + characters?.replaceAll?.("-", "");
}

const DATA_INPUT_CODE = "DP_1";

// Generate flow id
const generateUniqueID = (length) => generateUUID();

/**
 * The function `convertArrayToObject` takes an array of objects and converts it into an object where
 * each object is a property with its `id` as the key.
 * @param [arr] - The `arr` parameter is an array that contains objects. Each object in the array
 * should have an `id` property.
 */
const convertArrayToObject = (arr = []) =>
  arr.reduce((prev, curr) => {
    return { ...prev, [curr?.id]: curr };
  }, {});

const logSlice = (payload) =>
  console.log("target", JSON.parse(JSON.stringify(payload)));

/**
 * Updates the default values of a Redux slice with new values based on a given selector ID.
 *
 * @param {Object} defaults - An object containing the default values of a Redux slice.
 * @param {string || string[]} selectorId - A string representing the ID of the selector to be updated.
 * @param {Object || object[]} values - An object containing the new values to be assigned to the selector.
 * @returns {Object} - An object containing the updated default values of a Redux slice.
 */
const sliceUpdater = (defaults, selectorId, values) => {
  if (!selectorId) return defaults;
  
  if (Array.isArray(selectorId)) {
    let updatedValues = Array.isArray(values) ? values : [values];
    let updatedSelectors = Array.isArray(selectorId)
      ? selectorId
      : [selectorId];
    let updatedObject = {};
    updatedSelectors.forEach((selector, index) => {
      updatedObject[selector] = updatedValues[index];
    });

    return { ...defaults, ...updatedObject };
  }
  return { ...defaults, [selectorId]: values };
};

/**
 * Merges two objects into a single object by combining their properties.
 * This function is commonly used in Redux applications to update the state object with new values.
 *
 * @param {Object} previous - The previous state object.
 * @param {Object} value - The object containing the new values to be merged with the previous state.
 * @returns {Object} - A new object that contains all the properties of both the previous and value objects.
 */
const merge = (previous, value) => {
  return { ...previous, ...value };
};

// TODO: convert array data to object at the state update level.

/**
 * The data structure of each state on full blown data will look like
 * the following below.
 *
 *  @example initialState
 *  initialState = {
 *    activeTab: 12,`
 *    tabs: {
 *       12: {
 *          projectId: 12,
 *          lastBlockId: 2.........,
 *          previousBlockId: 5.....
 *          nodeCount: 1,
 *          pipeCount: 1,
 *          totalFlows: [fl_1, ....],
 *          sideNavigation: SidemenuOptions.FUNCTIONS,
 *          activeNavigation: SidemenuOptions.FUNCTIONS,
 *          studioType: "Pipeline",
 *            project_compiled_models: null,
 *            project_compiled_weights: null,
 *          nodes: [{
 *              id: 2,
 *              data: {
 *                id: 1,
 *                label: "HI",
 *                function_code: "",
 *                block_id: 1,
 *                state: {}
 *              }
 *          }],
 *          edges: [],
 *          pipelines: {
 *            nodeCount: 1,
 *            nodes: [{....}],
 *            edges: [{.....}]
 *          }
 *       },
 *    }
 * }
 */
export const flowSlice = createSlice({
  name: "flow",
  initialState,
  reducers: {
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },
    addTab: (state, action) => {
      let { project_id, ...values } = action.payload;

      if (isUndefined(project_id) || isNull(project_id)) return;

      state.tabs[project_id] = {
        project_id,
        nodeCount: 0,
        ...values,
        sideNavigation: SidemenuOptions.FUNCTIONS,
        activeNavigation: SidemenuOptions.FUNCTIONS,
        studioType: STUDIO_TYPE.FUNCTIONS,
        switcherPreviewers: {
          function: null,
          pipeline: null,
        },
      };
    },
    updateTab: (state, action) => {
      let {
        nodes,
        edges,
        app_id,
        name,
        viewport,
        projectId,
        pipelines,
        studioType,
        activePipe,
        project_id,
        lastBlockId,
        lastFetched,
        nodeCount = 0,
        pipeCount = 0,
        sideNavigation,
        activeNavigation,
        switcherPreviewers,
        project_compiled_weights,
        project_compiled_models,
      } = action.payload;

      if (isUndefined(project_id) || isNull(project_id)) return;

      const compiled_models = isNull(project_compiled_models)
        ? state.tabs[project_id]?.project_compiled_models
        : project_compiled_models;

      const compiled_weights = isNull(project_compiled_weights)
        ? state.tabs[project_id]?.project_compiled_weights
        : project_compiled_weights;

      // Update the particular project id
      state.tabs[project_id] = {
        nodes: Array.isArray(nodes) ? convertArrayToObject(nodes) : nodes,
        edges: Array.isArray(edges) ? convertArrayToObject(edges) : edges,
        app_id,
        name,
        viewport,
        projectId,
        activePipe,
        project_id,
        nodeCount,
        pipeCount,
        studioType: studioType ?? STUDIO_TYPE.FUNCTIONS,
        pipelines: Array.isArray(pipelines)
          ? convertArrayToObject(pipelines)
          : pipelines,
        lastFetched,
        lastBlockId,
        sideNavigation,
        activeNavigation,
        switcherPreviewers,
        project_compiled_weights: compiled_weights,
        project_compiled_models: compiled_models,
      };
    },
    updateCompiled: (state, action) => {
      const { project_id, project_compiled_weights, project_compiled_models } =
        action.payload;

      // logger(project_compiled_weights);

      state.tabs[project_id] = {
        ...state.tabs[project_id],
        project_compiled_weights,
        project_compiled_models,
      };
    },
    removeTab: (state, action) => {
      let copy = { ...state.tabs };

      delete copy[action.payload];

      state.tabs = copy;
    },

    // * Get the current tab details
    // * Extract the payload data for creating a new node
    // * Generate a new flow id if the function block dropped
    // is data input ('DP_1') else use parent flow id
    // * Generate a new node property.
    // *
    addNode: (state, action) => {
      // assign the active tab id.
      let currentTab = state.tabs[state.activeTab];

      // destructure the payload values.
      let { x, y, function_code, function_name } = action.payload;

      // assign data_input function code to a constant.
      const DATA_INPUT_CODE = "DP_1";

      // assign the new flow id generated.
      const flowId = generateUniqueID();

      // toggles between data input and other blocks to assign flow id.
      let flow_id = function_code === DATA_INPUT_CODE ? flowId : null;

      const newNode = {
        id: currentTab?.nodeCount?.toString() ?? "0", // use in place of parent_id
        data: {
          id: currentTab?.nodeCount?.toString() ?? "0",
          label: NODE_LABEL.function,
          function_code,
          function_name,
          block_id: currentTab?.nodeCount + 1 ?? 0,
          flow_id,
          state: BlockStates.IDLE,
        },
        position: { x, y },
        type: "dataSplitNode",
      };

      state.tabs[state.activeTab] = merge(currentTab, {
        data: { isLocked: false },
        nodes: merge(currentTab?.nodes, { [newNode?.id]: newNode }),
        nodeCount: currentTab?.nodeCount + 1,
      });
    },
    onUpdateNodeState: (state, action) => {
      let currentTab = state.tabs[state?.activeTab];
      let { id, ...values } = action?.payload;
      let nodes = currentTab?.nodes;
      let node = nodes[id];

      let updatedNode = merge(node, {
        data: merge(node?.data, { state: values?.state }),
      });

      // converting previous array to object and updating
      // the nodes state.
      let updatedNodes = merge(nodes, { [id]: updatedNode });

      // Updating state.
      state.tabs[state.activeTab] = merge(currentTab, { nodes: updatedNodes });
    },
    onUpdateNodeError: (state, action) => {
      let currentTab = state.tabs[state?.activeTab];
      let { id, value } = action?.payload;
      let nodes = currentTab?.nodes;
      let node = nodes[id];

      let updatedNode = merge(node, {
        data: merge(node.data, { output_error: { message: value } }),
      });

      // converting previous array to object and updating
      // the nodes state.
      let updatedNodes = merge(nodes, { [id]: updatedNode });

      state.tabs[state.activeTab] = merge(currentTab, { nodes: updatedNodes });
    },
    onUpdateNodeSuccess: (state, action) => {
      let currentTab = state.tabs[state?.activeTab];
      let { id, value } = action?.payload;
      let nodes = currentTab?.nodes;
      let node = nodes[id];

      let updatedNode = merge(node, {
        data: merge(node.data, { output: { ...value } }),
      });

      // converting previous array to object and updating
      // the nodes state.
      let updatedNodes = merge(nodes, { [id]: updatedNode });

      state.tabs[state.activeTab] = merge(currentTab, { nodes: updatedNodes });
    },
    onUpdateNodeNotes: (state, action) => {
      let currentTab = state.tabs[state?.activeTab];
      let { id, notes } = action?.payload;
      let nodes = currentTab?.nodes;
      let node = nodes[id];

      let updatedNode = merge(node, { data: merge(node.data, { notes }) });

      let updatedNodes = merge(nodes, { [id]: updatedNode });

      state.tabs[state.activeTab] = merge(currentTab, { nodes: updatedNodes });
    },
    onRemoveNode: (state, action) => {
      let currentTab = state.tabs[state.activeTab];
      let nodeId = action.payload;

      let nodes = { ...currentTab?.nodes };
      let edges = { ...currentTab?.edges };

      nodes[nodeId]?.edgesId?.forEach((item) => delete edges[item]);

      delete nodes[nodeId];

      state.tabs[state.activeTab] = merge(currentTab, { nodes, edges });
    },
    onRemoveEdges: (state, action) => {
      let currentTab = state.tabs[state.activeTab];
      let edgeId = action.payload;
      let edges = { ...currentTab?.edges };
      let nodes = { ...currentTab?.nodes };

      let edge = edges[edgeId];
      let updateTargetNodeEdgeId = nodes[edge?.target]?.edgeId?.filter?.(
        (item) => item !== edgeId
      );
      let updateSourceNodeEdgeId = nodes[edge?.source]?.edgeId?.filter?.(
        (item) => item !== edgeId
      );

      let updatedNodes = sliceUpdater(
        nodes,
        [edge?.target, edge?.source],
        [
          merge(nodes[edge?.target], {
            edgeId: updateTargetNodeEdgeId,
          }),
          merge(nodes[edge?.source], {
            edgeId: updateSourceNodeEdgeId,
          }),
        ]
      );

      delete edges[edgeId];

      state.tabs[state.activeTab] = merge(currentTab, {
        nodes: updatedNodes,
        edges,
      });
    },
    onNodeChanges: (state, action) => {
      let currentTab = state.tabs[state.activeTab];
      let { id, type, ...values } = action?.payload?.[0];
      let nodes = currentTab?.nodes;
      // let node = nodes[id];

      let updateNodes = applyNodeChanges(action.payload, Object.values(nodes));

      if (isExact(type, "position")) {
        state.hasMoved = true;
      }

      // let updatedNode = { ...node, ...values };
      let updatedNodes = merge(nodes, { ...convertArrayToObject(updateNodes) });

      state.tabs[state.activeTab] = merge(currentTab, { nodes: updatedNodes });
    },
    addNodeFlowId: (state, action) => {
      let currentTab = state.tabs[state?.activeTab];
      let { source, target } = action.payload;

      let sourceNode = currentTab?.nodes?.[source];
      let targetNode = currentTab?.nodes?.[target];

      let flow_id = sourceNode?.data?.flow_id;

      let updatedNode = merge(targetNode, {
        data: merge(targetNode?.data, { flow_id }),
      });

      let updatedNodes = sliceUpdater(
        currentTab?.nodes,
        targetNode?.id,
        updatedNode
      );

      state.tabs[state.activeTab] = merge(currentTab, { nodes: updatedNodes });
    },
    onLockNode: (state, action) => {
      let currentTab = state.tabs[state.activeTab];
      let { id, isLocked } = action.payload;
      let nodes = currentTab?.nodes;
      let node = currentTab?.nodes[id];

      let updateNode = merge(node, { data: merge(node.data, { isLocked }) });

      // converting previous array to object and updating
      // the nodes state.
      let updatedNodes = merge(nodes, { [id]: updateNode });

      state.tabs[state.activeTab].nodes = updatedNodes;
    },
    onEdgesChanges: (state, action) => {
      let currentTab = state.tabs[state.activeTab];
      let { id, source, target, ...values } = action?.payload?.[0];
      let edges = currentTab?.edges;
      let edge = edges[id];

      let updatedEdge = merge(edge, values);

      let updatedEdges = merge(edges, { [id]: updatedEdge });

      state.tabs[state.activeTab] = merge(currentTab, { edges: updatedEdges });
    },
    onConnect: (state, action) => {
      let currentTab = state.tabs[state.activeTab];
      let connection = action?.payload;

      let edgePayload = { ...connection, type: "buttonedge" };
      let edges = addEdge(edgePayload, []);
      let id = edges?.[0]?.id;
      let nodes = currentTab?.nodes;
      let targetNode = nodes?.[connection?.target];
      let sourceNode = nodes?.[connection?.source];

      // // if(currentTab?.){}
      // // console.log(action);
      let updatedTargetNode = merge(targetNode, {
        edgesId: Array.isArray(targetNode?.edgesId) ? [id] : [id],
      });

      let updatedSourceNode = merge(sourceNode, {
        edgesId: Array.isArray(sourceNode?.edgesId)
          ? [...sourceNode?.edgesId, id]
          : [id],
      });

      let updatedNodes = merge(nodes, {
        [connection?.target]: updatedTargetNode,
        [connection?.source]: updatedSourceNode,
      });

      state.tabs[state.activeTab] = merge(currentTab, {
        nodes: updatedNodes,
        edges: merge(currentTab?.edges, { [id]: edges?.[0] }),
      });
    },

    addPipeTab: (state, action) => {
      // assign the active tab id.
      let currentTab = state.tabs[state.activeTab];

      let pipeWindow = {
        nodes: {},
        edges: {},
        name: "unknown",
        id: generateUniqueID(),
        img: DefaultPipe,
      };

      state.tabs[state.activeTab] = merge(currentTab, {
        activePipe: pipeWindow.id,
        pipelines: merge(currentTab.pipelines, {
          [pipeWindow?.id]: pipeWindow,
        }),
      });
    },
    onPipeLock: (state, action) => {
      let currentTab = state.tabs[state.activeTab];
      let pipelines = currentTab?.pipelines;
      let pipeWindow = pipelines[currentTab?.activePipe];
      let node = pipeWindow?.nodes[action.payload.id];

      let updateNode = merge(node, {
        data: merge(node.data, { isLocked: action.payload.isLocked }),
      });

      let updatedNodes = merge(pipeWindow?.nodes, {
        [action.payload.id]: updateNode,
      });

      let updatedPipeWindow = merge(pipeWindow, { nodes: updatedNodes });

      state.tabs[state.activeTab].pipelines = sliceUpdater(
        pipelines,
        currentTab?.activePipe,
        updatedPipeWindow
      );
    },
    resetPipeWindow: (state, action) => {
      let currentTab = state.tabs[state.activeTab];

      state.tabs[state.activeTab] = merge(currentTab, { pipelines: {} });
    },
    migratePipelines: (state, action) => {
      let window = action.payload;
      let currentTab = state.tabs[state.tabs];

      state.tabs[state.activeTab].pipelines = merge(
        currentTab?.pipelines ?? {},
        { ...convertArrayToObject(window) }
      );
    },
    updatePipeTab: (state, action) => {
      const { activePipe, ...values } = action.payload;
      let currentTab = state.tabs[state?.activeTab];
      let pipelines = currentTab?.pipelines;
      let pipeWindow = pipelines[activePipe];

      let update = merge(pipeWindow, values);

      state.tabs[state.activeTab] = merge(currentTab, {
        pipelines: merge(currentTab?.pipelines, { [activePipe]: update }),
      });
    },
    addPipeNode: (state, action) => {
      /**
       * Task: To add a new pipe to an existing list of
       * pipes (nodes).
       *
       * 1. Extract the current / active Tab details
       * 2. Extract all pipelines to a variable
       * 3. Extract the active pipeWindow (i.e activePipe)
       * 4. Generate a new pipe Id
       * 5. Extract the particular pipeWindow information to a variable
       * 6. Extract the pipes (nodes) under the pipeWindow
       * 7. Generate a new flow id
       * 7. Get last pipe id created
       * 7. Append the new pipe to the list of pipes
       * 8. Update the pipes, pipeWindow and pipelines
       */
      let currentTab = state.tabs[state.activeTab];
      let pipelines = currentTab?.pipelines;
      let activePipe = currentTab?.activePipe;
      let lastPipeId = currentTab?.pipeCount?.toString() ?? "0";

      let pipeWindow = pipelines[activePipe];
      let pipes = pipeWindow?.nodes;

      // destructure the payload values.
      let {
        x,
        y,
        name,
        function_code,
        function_name,
        properties,
        on_pipeline,
        blockType,
      } = action.payload;

      // // assign the new flow id generated.
      const flowId = generateUniqueID();

      // toggles between data input and other blocks to assign flow id.
      let flow_id = flowId;
      let newPipeId = (parseInt(lastPipeId) + 1).toString();

      const newPipe = {
        id: newPipeId,
        data: {
          id: newPipeId,
          name,
          flow_id,
          label: NODE_LABEL.pipe,
          function_code,
          function_name,
          properties,
          block_id: parseInt(newPipeId) ?? 0,
          state: BlockStates.IDLE,
        },
        position: { x, y },
        type: blockType,
        on_pipeline,
      };

      let updatedPipes = merge(pipes, { [newPipeId]: newPipe });

      const updateWindow = merge(pipeWindow, { nodes: { ...updatedPipes } });

      state.tabs[state.activeTab] = merge(currentTab, {
        pipeCount: parseInt(newPipeId),
        pipelines: merge(pipelines, { [activePipe]: updateWindow }),
      });
    },
    onUpdatePipelineNodeState: (state, action) => {
      let currentTab = state.tabs[state?.activeTab];
      let { id, ...values } = action.payload;

      let pipeWindow = currentTab?.pipelines?.[currentTab?.activePipe];
      let pipe = pipeWindow?.nodes[id];

      let updatedNode = merge(pipe, {
        data: merge(pipe?.data, { state: values.state }),
      });

      let updatedNodes = merge(pipeWindow?.nodes, {
        [id]: updatedNode,
      });

      let updatedPipeWindow = merge(pipeWindow, { nodes: updatedNodes });

      state.tabs[state.activeTab].pipelines = sliceUpdater(
        currentTab?.pipelines,
        currentTab?.activePipe,
        updatedPipeWindow
      );
    },
    onPipeRemoveNode: (state, action) => {
      let currentTab = state.tabs[state.activeTab];
      let id = action.payload;

      let pipelines = currentTab?.pipelines;
      let pipeWindow = pipelines?.[currentTab?.activePipe];

      let nodes = { ...pipeWindow?.nodes };
      let edges = { ...pipeWindow?.edges };

      nodes[id]?.edgesId?.forEach((item) => delete edges[item]);

      delete nodes[id];
      delete edges[id]

      let updatePipeWindow = merge(pipeWindow, { nodes, edges });

      state.tabs[state.activeTab].pipelines = sliceUpdater(pipelines, currentTab?.activePipe, updatePipeWindow);
    },
    onPipeRemoveEdges: (state, action) => {
      let currentTab = state.tabs[state.activeTab];
      let edgeId = action.payload;

      let pipelines = currentTab?.pipelines;
      let pipeWindow = pipelines?.[currentTab?.activePipe];

      // Create a new reference to nodes and edges
      let nodes = { ...pipeWindow?.nodes };
      let edges = { ...pipeWindow?.edges };

      let edge = edges[edgeId];
      let updateTargetNodeEdgeId = nodes[edge?.target]?.edgesId?.filter?.(
        (item) => item !== edgeId
      );
      let updateSourceNodeEdgeId = nodes[edge?.source]?.edgesId?.filter?.(
        (item) => item !== edgeId
      );

      let updateNodes = merge(nodes, {
        [edge?.target]: merge(nodes[edge?.target], {
          edgeId: updateTargetNodeEdgeId,
        }),
        [edge?.source]: merge(nodes[edge?.source], {
          edgeId: updateSourceNodeEdgeId,
        }),
      });

      delete edges[edgeId];

      let updatePipeWindow = merge(pipeWindow, {
        nodes: updateNodes,
        edges: edges ?? {},
      });

      // logSlice(updatePipeWindow);

      state.tabs[state.activeTab].pipelines = sliceUpdater(
        currentTab?.pipelines,
        currentTab?.activePipe,
        updatePipeWindow
      );
    },
    onPipeNodeChanges: (state, action) => {
      let { id, type, ...values } = action?.payload?.[0];
      let currentTab = state.tabs[state.activeTab];
      let pipelines = currentTab?.pipelines;
      let pipeWindow = pipelines?.[currentTab?.activePipe];
      let pipes = pipeWindow?.nodes;
      let pipe = pipes[id];

      // Updating pipe or node
      let updatedPipe = merge(pipe, values);

      // Updates the pipes or nodes
      let updatedPipes = Array.isArray(pipes)
        ? sliceUpdater(convertArrayToObject(pipes), id, updatedPipe)
        : merge(pipes, { [id]: updatedPipe });

      // Updating the pipeline or pipeWindow
      let updatedPipeWindow = merge(pipeWindow, { nodes: { ...updatedPipes } });

      state.tabs[state.activeTab].pipelines = sliceUpdater(
        pipelines,
        currentTab?.activePipe,
        updatedPipeWindow
      );
    },
    onPipeEdgesChanges: (state, action) => {
      let currentTab = state.tabs[state.activeTab];

      // let pipeline = currentTab?.pipelines?.find(
      //   (pipe) => pipe?.id === currentTab?.activePipe
      // );

      // let updatePipeTab = currentTab?.pipelines?.map((pipe) => {
      //   if (pipe?.id === currentTab?.activePipe) {
      //     return {
      //       ...pipe,
      //       edges: applyEdgeChanges(action.payload, pipeline?.edges ?? []),
      //     };
      //   }
      //   return pipe;
      // });

      // state.tabs[state.activeTab].pipelines = updatePipeTab;
    },
    onPipeConnect: (state, action) => {
      let currentTab = state.tabs[state.activeTab];
      let connection = action?.payload;
      let pipelines = currentTab?.pipelines;
      let pipeWindow = pipelines?.[currentTab?.activePipe];

      let edgePayload = { ...connection, type: "buttonedge" };
      let createdEdge = addEdge(edgePayload, []);
      let id = createdEdge?.[0]?.id;

      let { nodes, edges } = pipeWindow;
      let targetNode = nodes?.[connection?.target] ?? {};
      let sourceNode = nodes?.[connection?.source] ?? {};

      let updatedTargetNode = merge(targetNode, {
        edgesId: Array.isArray(targetNode?.edgesId)
          ? [...targetNode?.edgesId, id]
          : [id],
      });
      let updatedSourceNode = merge(sourceNode, {
        edgesId: Array.isArray(sourceNode?.edgesId)
          ? [...sourceNode?.edgesId, id]
          : [id],
      });

      let updatedNodes = sliceUpdater(
        nodes,
        [connection?.target, connection?.source],
        [updatedTargetNode, updatedSourceNode]
      );

      let updatePipelines = sliceUpdater(
        pipelines,
        currentTab?.activePipe,
        merge(pipeWindow, {
          nodes: updatedNodes,
          edges: merge(edges, { [id]: createdEdge?.[0] }),
        })
      );

      // logSlice(updatePipelines);

      state.tabs[state.activeTab].pipelines = updatePipelines;
    },
    addNodePipeFlowId: (state, action) => {
      let currentTab = state.tabs[state?.activeTab];
      let { source, target } = action.payload;

      let pipeWindow = currentTab?.pipelines?.[currentTab?.activePipe];

      let sourceNode = pipeWindow?.nodes?.[source];
      let targetNode = pipeWindow?.nodes?.[target];

      let flow_id = sourceNode?.data?.flow_id;

      let updatedNode = merge(targetNode, {
        data: {
          ...targetNode.data,
          flow_id,
        },
      });

      let updatePipeWindow = merge(pipeWindow, {
        nodes: {
          ...pipeWindow?.nodes,
          [target]: updatedNode,
        },
      });

      let updatedPipelines = sliceUpdater(
        currentTab?.pipelines,
        currentTab?.activePipe,
        updatePipeWindow
      );

      // logSlice(updatedPipelines);

      state.tabs[state.activeTab].pipelines = updatedPipelines;
    },
    onResetProject: (state, action) => {
      let currentTab = state?.tabs[state?.activeTab];

      state.tabs[state.activeTab] = {
        ...currentTab,
        nodes: {},
        edges: {},
        nodeCount: 0,
        pipeCount: 0,
        sideNavigation: SidemenuOptions.FUNCTIONS,
        activeNavigation: SidemenuOptions.FUNCTIONS,
        switcherPreviewers: {
          function: null,
          pipeline: null,
        },
      };
    },
    onResetPipeline: (state, action) => {
      let currentTab = state?.tabs[state?.activeTab];

      let pipeWindow = currentTab?.pipelines?.[currentTab?.activePipe];

      let updatePipeWindow = merge(pipeWindow, {
        nodes: {},
        edges: {},
      });

      let updatedPipelines = sliceUpdater(
        currentTab?.pipelines,
        currentTab?.activePipe,
        updatePipeWindow
      );

      state.tabs[state.activeTab].pipelines = updatedPipelines;
    },
    onUpdatePipelineNodeError: (state, action) => {
      let currentTab = state.tabs[state?.activeTab];
      let id = action.payload?.id;
      let pipeWindow = currentTab?.pipelines?.[currentTab?.activePipe];
      let pipe = pipeWindow?.nodes[id];

      let updatedNode = merge(pipe, {
        data: merge(pipe.data, {
          output_error: { message: action.payload?.value },
        }),
      });

      let updatedNodes = merge(pipeWindow?.nodes, {
        [id]: updatedNode,
      });

      let updatePipeWindow = merge(pipeWindow, {
        nodes: updatedNodes,
      });

      let updatedPipelines = sliceUpdater(
        currentTab?.pipelines,
        currentTab?.activePipe,
        updatePipeWindow
      );

      state.tabs[state.activeTab].pipelines = updatedPipelines;
    },
    onUpdatePipelineNodeSuccess: (state, action) => {
      let currentTab = state.tabs[state?.activeTab];
      let id = action.payload?.id;
      let pipeWindow = currentTab?.pipelines?.[currentTab?.activePipe];
      let pipe = pipeWindow?.nodes[id];

      let updatedNode = merge(pipe, {
        data: merge(pipe.data, { output: { ...action.payload?.value } }),
      });

      let updatedNodes = sliceUpdater(pipeWindow?.nodes, id, updatedNode);

      let updatePipeWindow = merge(pipeWindow, {
        nodes: updatedNodes,
      });

      state.tabs[state.activeTab].pipelines = sliceUpdater(
        currentTab?.pipelines,
        currentTab?.activePipe,
        updatePipeWindow
      );
    },
    setTotalFlows: (state, action) => {
      state.totalFlows = action.payload;
    },
    reset: () => initialState,
    setHasMoved: (state, action) => {
      state.hasMoved = action.payload;
    },

    // flow function
    onFlowInstance: (state, action) => {
      state.flowInstance = action.payload;
    },

    // Side bar navigation
    setNavigation: (state, action) => {
      const { project_id, id } = action.payload;

      if (isUndefined(project_id) || isNull(project_id)) return;

      let currentProject = state.tabs[project_id];

      if (id === SidemenuOptions.SWITCHER) {
        state.tabs[project_id] = merge(currentProject, {
          sideNavigation: null,
          activePipe: null,
          studioType: STUDIO_TYPE.SWITCHER,
          activeNavigation: id,
        });
        return;
      }

      if (currentProject?.sideNavigation === id) {
        state.tabs[project_id] = merge(currentProject, {
          sideNavigation: null,
        });
        return;
      }

      let updatedTabs = sliceUpdater(
        state.tabs,
        project_id,
        merge(currentProject, {
          sideNavigation: id,
          activeNavigation: id,
        })
      );

      state.tabs = updatedTabs;
    },
    setStudioType: (state, action) => {
      let { page, project_id, pipeId } = action.payload;
      let currentTab = state.tabs[project_id];

      let updatedTabs = sliceUpdater(
        state.tabs,
        project_id,
        merge(currentTab, {
          studioType: page,
          activePipe: pipeId ? pipeId : null,
        })
      );

      state.tabs = updatedTabs;
    },
    setSwitcherPreviewer: (state, action) => {
      const { project_id, type, url, pipeWindowId } = action.payload;
      let currentTab = state.tabs[state?.activeTab];

      if (!type) return;

      let currentSwitcher = currentTab?.switcherPreviewers;
      let pipelines = currentTab?.pipelines;
      let pipeWindow = pipelines?.[pipeWindowId];

      if (isExact(type, "pipeline")) {
        let updatedPipeWindow = merge(pipeWindow, {
          img: url,
        });

        state.tabs[state.activeTab].pipelines = sliceUpdater(
          pipelines,
          pipeWindowId,
          updatedPipeWindow
        );

        return;
      }

      state.tabs[state?.activeTab].switcherPreviewers = merge(currentSwitcher, {
        [type]: url,
      });
    },
  },
});

export const {
  setProjectId,
  addTab,
  // Adds a new tab to the previous list
  addPipeTab,
  updatePipeTab,
  updateTab,
  onConnect,
  onEdgesChanges,
  onNodeChanges,
  setActiveTab,
  removeTab,
  addNode,
  addNodeBlockId,
  onResetProject,
  onUpdateNodeState,
  onRemoveNode,
  onUpdateNodeError,
  onUpdateNodeSuccess,
  onRemoveEdges,
  onFlowInstance,
  onLockNode,
  onPipeLock,
  setTotalFlows,
  reset,
  setNavigation,
  setSwitcherPreviewer,
  addPipeNode,
  onPipeNodeChanges,
  onPipeConnect,
  onPipeEdgesChanges,
  onPipeRemoveEdges,
  onPipeRemoveNode,
  onUpdatePipelineNodeError,
  onUpdatePipelineNodeSuccess,
  onUpdatePipelineNodeState,
  updateCompiled,
  addNodeFlowId,
  addNodePipeFlowId,
  onResetPipeline,
  onUpdateNodeNotes,
  resetPipeWindow,
  setHasMoved,
  migratePipelines,
  setStudioType,
} = flowSlice.actions;

export const selectActiveTab = (state) => state?.flow?.activeTab;
export const selectCurrentTabNode = (state) => (currentTab) =>
  state.flow?.tabs?.[currentTab];
export const selectCurrentTabEdges = (state) => (currentTab) =>
  state.flow?.tabs?.[currentTab]?.edges;
export const selectTabs = (state) => Object.entries(state?.flow?.tabs);
export const selectLastBlockID = (state) => (currentTab) =>
  state?.flow?.tabs?.[currentTab]?.lastBlockId;
export const selectIsLocked = (state) => (currentTab, id) =>
  state?.flow?.tabs?.[currentTab]?.nodes[id]?.data?.isLocked;
export const selectNumOfFlows = (state) => (currentTab) =>
  state?.flow?.tabs?.[currentTab]?.totalFlows;

export const selectFlowInstance = (state) => state?.flow?.flowInstance;
export const selectSideNavigation = (state) => (currentTab) =>
  state?.flow?.tabs?.[currentTab]?.sideNavigation;
export const selectStudioType = (state) => (currentTab) =>
  state?.flow?.tabs?.[currentTab]?.studioType;
export const selectActiveNavigation = (state) => (currentTab) =>
  state?.flow?.tabs?.[currentTab]?.activeNavigation;
export const selectHasBlockMoved = (state) => state.flow.hasMoved;

export const selectPreviewer = (state) => (currentTab) =>
  state?.flow?.tabs?.[currentTab]?.switcherPreviewers;

export const selectCurrentPipelineNode = (state) => (currentTab) =>
  state?.flow?.tabs?.[currentTab]?.pipelines;

export default flowSlice.reducer;
